import PageLoader from '@/components/PageLoader';
import React, { ComponentType, ReactNode, useEffect, useState } from 'react';

interface FallbackProps {
  retry: () => void;
  error: Error | null;
}

export type SupportedLocale = 'ja' | 'en';

type UseLocaleReturn = {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  t: (key: string, values?: Record<string, unknown>) => string;
  [key: string]: unknown;
};

type UseLocaleType = () => UseLocaleReturn;

let useLocaleRef: UseLocaleType | null = null;

const federatedComponentCache = new Map<string, ComponentType<unknown>>();

const importFn = (componentName: string): Promise<unknown> => {
  switch (componentName) {
    case 'Translation':
      return import('hoslog-intl/' + 'Translation');
    case 'LocaleContextController':
      return import('hoslog-intl/' + 'LocaleContextController');
    case 'LocaleSwitch':
      return import('hoslog-intl/' + 'LocaleSwitch');
    case 'useLocale':
      return import('hoslog-intl/' + 'useLocale');
    default:
      throw new Error(`Component ${componentName} not found`);
  }
};

const createDynamicComponent = <P extends object>(
  componentName: string,
  FallbackComponent: ComponentType<P & FallbackProps> | null = null,
): React.FC<P> => {
  const DynamicComponentWrapper: React.FC<P> = (props) => {
    const [Component, setComponent] = useState<ComponentType<P> | null>(
      () => (federatedComponentCache.get(componentName) as ComponentType<P>) || null,
    );
    const [error, setError] = useState<Error | null>(null);
    const [retryKey, setRetryKey] = useState(0);

    useEffect(() => {
      if (Component) return;

      let isMounted = true;

      importFn(componentName)
        .then(async (moduleValue) => {
          const mod = moduleValue as { default: ComponentType<unknown> };
          if (componentName === 'LocaleContextController' && !useLocaleRef) {
            const localeModule = (await importFn('useLocale')) as {
              useLocale?: UseLocaleType;
              default?: UseLocaleType;
            };
            useLocaleRef = localeModule.useLocale ?? localeModule.default ?? null;
            if (!useLocaleRef) throw new Error('The remote useLocale module did not export a hook.');
          }

          if (isMounted) {
            const component = mod.default;
            federatedComponentCache.set(componentName, component);
            setComponent(() => mod.default as ComponentType<P>);
          }
        })
        .catch((err) => {
          if (isMounted) {
            setError(err instanceof Error ? err : new Error(String(err)));
          }
        });

      return () => {
        isMounted = false;
      };
      // keep existing behavior: only re-run on retry
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [retryKey]);

    const retry = () => {
      setError(null);
      setRetryKey((prev) => prev + 1);
    };

    if (error && FallbackComponent) {
      return <FallbackComponent {...props} error={error} retry={retry} />;
    }

    if (!Component) {
      return <PageLoader />;
    }

    return <Component key={retryKey} {...props} />;
  };

  return DynamicComponentWrapper;
};

/**
 * ✅ Updated: useLocale typing now supports { locale, setLocale, t } (plus any other fields)
 * This will NOT break existing callers that only use `t`.
 */
const loadUseLocale = async (): Promise<UseLocaleType | null> => {
  if (useLocaleRef) return useLocaleRef;

  try {
    const mod = (await importFn('useLocale')) as {
      useLocale?: UseLocaleType;
      default?: UseLocaleType;
    };

    useLocaleRef = mod.useLocale ?? mod.default ?? null;
    return useLocaleRef;
  } catch {
    return null;
  }
};

const useDynamicLocale = (): UseLocaleReturn => {
  if (!useLocaleRef) {
    throw new Error('useLocale has not been loaded yet. Call loadUseLocale() before using the hook.');
  }

  return useLocaleRef();
};

interface TranslationProps {
  id: string;
  values?: Record<string, string | number>;
  [key: string]: unknown;
}

interface LocaleContextControllerProps {
  children: ReactNode;
  defaultLocale?: string;
  [key: string]: unknown;
}

interface SwitchLocaleProps {
  onChange?: (locale: string) => void;
  [key: string]: unknown;
}

const TranslationFallback: React.FC<TranslationProps & FallbackProps> = () => <span>⚠️</span>;

const LocaleContextControllerFallback: React.FC<LocaleContextControllerProps & FallbackProps> = ({ children }) => (
  <>
    {children}
    <div className="text-[0.7rem] text-destructive-text p-1">⚠️</div>
  </>
);

const SwitchLocaleFallback: React.FC<SwitchLocaleProps & FallbackProps> = () => <span>⚠️</span>;

const Translation = createDynamicComponent<TranslationProps>('Translation', TranslationFallback);
const LocaleContextController = createDynamicComponent<LocaleContextControllerProps>(
  'LocaleContextController',
  LocaleContextControllerFallback,
);
const ToggleLocale = createDynamicComponent<SwitchLocaleProps>('LocaleSwitch', SwitchLocaleFallback);

export { loadUseLocale, LocaleContextController, ToggleLocale, Translation, useDynamicLocale };
